import { useLayoutEffect } from 'react';
import type { InspectionFlag } from '../api/inspections';
import { nextUnreviewedIndex, type ReviewItem } from './inspectionGroups';

/** The keys 1-5 the grid and the viewer both answer to, in the flags' own order. */
const FLAG_KEYS: Record<string, InspectionFlag> = {
  '1': 'correct',
  '2': 'problem',
  '3': 'useless',
  '4': 'repeat',
  '5': 'mismatch',
};

/**
 * The 1-5/N keyboard shortcuts a review page answers to (InspectionReview.tsx,
 * build item 2): 1-5 flags the active capture (the viewer's own, or the
 * grid's), N jumps to the next unreviewed one, wrapping around `items` once.
 * Factored out so the campaign's own one-page review answers to the same
 * keys over every tour's items concatenated, not just one tour's — "next
 * pending" crossing from the last capture of one tour into the first of the
 * next is this function wrapping around the whole array, unchanged.
 *
 * The listener is put in a layout effect, in the commit that drew the
 * captures, not in a passive one a moment later: until it is replaced the
 * window still holds the listener of the render before, whose active capture
 * is the old one (none at all while the page loads), and a key pressed in
 * between does nothing or marks the wrong picture. A person cannot be that
 * fast; a test is, whenever the machine is busy enough for the passive effect
 * to slip a task (CI, 2026-10-11: the "2" right after the counter appeared
 * was dropped and no save was ever sent).
 */
export function useReviewShortcuts(
  items: ReviewItem[],
  activeIndex: number | null,
  viewerOpen: boolean,
  toggleFlag: (uploadId: number, flag: InspectionFlag) => void,
  setViewerIndex: (index: number) => void,
  setFocusedIndex: (index: number) => void,
) {
  useLayoutEffect(() => {
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT')) return;
      if (activeIndex === null) return;
      const flag = FLAG_KEYS[e.key];
      const at = items[activeIndex];
      if (flag && at?.type === 'capture') {
        e.preventDefault();
        toggleFlag(at.capture.uploadId, flag);
        return;
      }
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        const next = nextUnreviewedIndex(items, activeIndex);
        if (next === null) return;
        if (viewerOpen) setViewerIndex(next);
        else setFocusedIndex(next);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, activeIndex, viewerOpen, toggleFlag, setViewerIndex, setFocusedIndex]);
}
